// Lightweight, dependency-free i18n for the app.
//
// The active language is chosen in the Header language selector and persisted
// in localStorage under `appLanguage`. Switching languages dispatches a
// `languagechange` window event; the `useLanguage` hook subscribes to it so
// components re-render with the new strings.
//
// Supported languages: EN (English), DE (Deutsch), SE (Svenska).

export const LANGUAGE_STORAGE_KEY = "appLanguage";
export const DEFAULT_LANGUAGE = "EN";
export const SUPPORTED_LANGUAGES = ["EN", "DE", "SE"];

// Nested translation dictionary keyed by language codes.
const translations = {
	EN: {
		header: {
			logoTooltip: "Click on Logo to go to HomePage",
			appTitle: "Parts Tracking Tool",
			titleParts: "Parts Tracking Tool",
			titleVehiclePlanning: "Vehicle Planning",
			titleShopfloor: "Shopfloor Production",
			changeLanguage: "Change language",
			jiraTickets: "Jira tickets",
			createTicket: "Create Ticket",
			myTickets: "My Tickets",
			newReleases: "New Releases",
			userGuide: "User Guide",
			projectInfo: "Project Info",
			manageUsers: "Manage Users",
			manageBanner: "Manage Banner",
			fzgPlanning: "FZG Planning",
			shopfloorProduction: "Shopfloor Production",
			signOut: "Sign Out",
			roleAdmin: "Admin",
			roleUser: "User",
			nav: {
				supplyType: "Supply Type allocation",
				vehicleTracking: "Vehicle tracking",
				seriesTracking: "Series tracking",
			},
		},
		landing: {
			heroGreeting: "Hi! {name}, Let's start tracking",
			heroSubtitle:
				"A centralized digital platform design to streamline and manage supply allocation, vehicle tracking and series tracking across operations.",
			goToDashboard: "Go to Dashboard",
			lifecycleTitle: "Parts Tracking Lifecycle",
			continue: "Continue",
			comingSoon: "Coming Soon",
			footerRights:
				"© {year} MAN Trucks & Bus. All rights reserved.",
			footerConfidential:
				"This page and its contents are strictly confidential and intended solely for authorized users & parts trackers.",
			stages: {
				"supply-type": {
					title: "Supply Type Allocation",
					subtitle: "SE-Team Procurement, Allocation & Planning",
					description:
						"Initial procurement, parts categorization and requisition planning",
				},
				vff: {
					title: "Vehicle Tracking",
					subtitle: "Vorserien-Freigabe-Fahrzeug",
					description:
						"Track vehicle functions, supplier nomination and features development",
				},
				series: {
					title: "Series Tracking",
					subtitle: "Production Series Monitoring",
					description:
						"Monitor production series and manufacturing progress of parts",
				},
			},
		},
	},
	DE: {
		header: {
			logoTooltip: "Auf das Logo klicken, um zur Startseite zu gelangen",
			appTitle: "Parts Tracking Tool",
			titleParts: "Parts Tracking Tool",
			titleVehiclePlanning: "Fahrzeugplanung",
			titleShopfloor: "Shopfloor-Produktion",
			changeLanguage: "Sprache ändern",
			jiraTickets: "Jira-Tickets",
			createTicket: "Ticket erstellen",
			myTickets: "Meine Tickets",
			newReleases: "Neue Versionen",
			userGuide: "Benutzerhandbuch",
			projectInfo: "Projektinformationen",
			manageUsers: "Benutzer verwalten",
			manageBanner: "Banner verwalten",
			fzgPlanning: "FZG-Planung",
			shopfloorProduction: "Shopfloor-Produktion",
			signOut: "Abmelden",
			roleAdmin: "Admin",
			roleUser: "Benutzer",
			nav: {
				supplyType: "Belieferungszuordnung",
				vehicleTracking: "Fahrzeug-Tracking",
				seriesTracking: "Serien-Tracking",
			},
		},
		landing: {
			heroGreeting: "Hallo {name}, lass uns mit dem Tracking beginnen",
			heroSubtitle:
				"Eine zentrale digitale Plattform zur Optimierung und Verwaltung von Belieferungszuordnung, Fahrzeug-Tracking und Serien-Tracking über alle Abläufe hinweg.",
			goToDashboard: "Zum Dashboard",
			lifecycleTitle: "Teile-Tracking-Lebenszyklus",
			continue: "Weiter",
			comingSoon: "Demnächst verfügbar",
			footerRights:
				"© {year} MAN Truck & Bus. Alle Rechte vorbehalten.",
			footerConfidential:
				"Diese Seite und ihre Inhalte sind streng vertraulich und ausschließlich für autorisierte Benutzer und Teile-Tracker bestimmt.",
			stages: {
				"supply-type": {
					title: "Belieferungszuordnung",
					subtitle: "SE-Team Beschaffung, Zuordnung & Planung",
					description:
						"Erstbeschaffung, Teilekategorisierung und Bedarfsplanung",
				},
				vff: {
					title: "Fahrzeug-Tracking",
					subtitle: "Vorserien-Freigabe-Fahrzeug",
					description:
						"Verfolgung von Fahrzeugfunktionen, Lieferantennominierung und Funktionsentwicklung",
				},
				series: {
					title: "Serien-Tracking",
					subtitle: "Überwachung der Produktionsserie",
					description:
						"Überwachung der Produktionsserie und des Fertigungsfortschritts der Teile",
				},
			},
		},
	},
	SE: {
		header: {
			logoTooltip: "Klicka på logotypen för att gå till startsidan",
			appTitle: "Parts Tracking Tool",
			titleParts: "Parts Tracking Tool",
			titleVehiclePlanning: "Fordonsplanering",
			titleShopfloor: "Verkstadsproduktion",
			changeLanguage: "Byt språk",
			jiraTickets: "Jira-ärenden",
			createTicket: "Skapa ärende",
			myTickets: "Mina ärenden",
			newReleases: "Nya versioner",
			userGuide: "Användarguide",
			projectInfo: "Projektinformation",
			manageUsers: "Hantera användare",
			manageBanner: "Hantera banner",
			fzgPlanning: "FZG-planering",
			shopfloorProduction: "Verkstadsproduktion",
			signOut: "Logga ut",
			roleAdmin: "Admin",
			roleUser: "Användare",
			nav: {
				supplyType: "Allokering av leveranstyp",
				vehicleTracking: "Fordonsspårning",
				seriesTracking: "Serietillverkningsspårning",
			},
		},
		landing: {
			heroGreeting: "Hej {name}, låt oss börja spåra",
			heroSubtitle:
				"En central digital plattform utformad för att effektivisera och hantera leveransallokering, fordonsspårning och serietillverkningsspårning över hela verksamheten.",
			goToDashboard: "Gå till instrumentpanelen",
			lifecycleTitle: "Livscykel för delspårning",
			continue: "Fortsätt",
			comingSoon: "Kommer snart",
			footerRights:
				"© {year} MAN Truck & Bus. Alla rättigheter förbehållna.",
			footerConfidential:
				"Denna sida och dess innehåll är strikt konfidentiella och avsedda endast för behöriga användare och delspårare.",
			stages: {
				"supply-type": {
					title: "Allokering av leveranstyp",
					subtitle: "SE-team inköp, allokering & planering",
					description:
						"Inledande inköp, kategorisering av delar och rekvisitionsplanering",
				},
				vff: {
					title: "Fordonsspårning",
					subtitle: "Vorserien-Freigabe-Fahrzeug",
					description:
						"Spåra fordonsfunktioner, leverantörsnominering och funktionsutveckling",
				},
				series: {
					title: "Serietillverkningsspårning",
					subtitle: "Övervakning av produktionsserie",
					description:
						"Övervaka produktionsserien och tillverkningsförloppet för delar",
				},
			},
		},
	},
};

export function getStoredLanguage() {
	const stored =
		typeof localStorage !== "undefined"
			? localStorage.getItem(LANGUAGE_STORAGE_KEY)
			: null;
	return SUPPORTED_LANGUAGES.includes(stored) ? stored : DEFAULT_LANGUAGE;
}

// Resolve a dot-path (e.g. "landing.heroGreeting") within an object.
function resolvePath(obj, path) {
	return path.split(".").reduce((acc, key) => {
		if (acc && typeof acc === "object" && key in acc) return acc[key];
		return undefined;
	}, obj);
}

// Interpolate {placeholder} tokens from `vars`.
function interpolate(template, vars) {
	if (typeof template !== "string" || !vars) return template;
	return template.replace(/\{(\w+)\}/g, (match, name) =>
		name in vars ? String(vars[name]) : match,
	);
}

// Translate `key` for `language`, falling back to English then the raw key.
export function translate(language, key, vars) {
	const lang = SUPPORTED_LANGUAGES.includes(language)
		? language
		: DEFAULT_LANGUAGE;
	const value =
		resolvePath(translations[lang], key) ??
		resolvePath(translations[DEFAULT_LANGUAGE], key) ??
		key;
	return interpolate(value, vars);
}

export default translations;
